import React from "react";
import { useIntl } from "react-intl";

import { Urgency } from "lib/build/forms/htx/patientCondition";

import { ColumnProps } from "src/common/components/DataTable";
import { MobileColumnProps } from "src/common/components/MobileTable";
import { SocialSecurityNumber } from "src/common/components/SocialSecurityNumber";
import { StatusBadge } from "src/common/components/StatusBadge";
import { messages } from "src/modules/htx/patient-list/messages";
import { PatientStatus } from "src/modules/htx/patient-list/types";
import {
	compareNumberEvents,
	mapNumberEventToString,
	renderNumberEvent
} from "src/modules/shared/patient-list";

export function useDesktopColumnProps(): ColumnProps[] {
	const status = useStatusColumnDesktop();
	const socialSecurityNumber = useSocialSecurityNumberColumn();
	const name = useNameColumn();
	const age = useAgeColumn();
	const sex = useSexColumn();
	const city = useCityColumn();
	const weight = useWeightColumn();
	const bloodGroup = useBloodGroupColumn();

	const pvr = usePvrColumn();
	const waitingList = useWaitingListColumnDesktop();
	const urgency = useUrgencyColumn();
	const combinedTransplant = useCombinedTransplantColumn();

	return React.useMemo(
		() => [
			status,
			socialSecurityNumber,
			name,
			age,
			sex,
			city,
			weight,
			bloodGroup,
			pvr,
			waitingList,
			urgency,
			combinedTransplant
		],
		[
			status,
			socialSecurityNumber,
			name,
			age,
			sex,
			city,
			weight,
			bloodGroup,
			pvr,
			waitingList,
			urgency,
			combinedTransplant
		]
	);
}

export function useMobileColumnProps(): MobileColumnProps {
	const status = useStatusColumnMobile();
	const name = useNameColumn();
	const socialSecurityNumber = useSocialSecurityNumberColumn();

	const bloodGroup = useBloodGroupColumn();
	const age = useAgeColumn();
	const waitingList = useWaitingListColumnMobile();
	const weight = useWeightColumn();

	const pvr = usePvrColumn();
	const urgency = useUrgencyColumn();
	const combinedTransplant = useCombinedTransplantColumn();

	return React.useMemo(
		() => ({
			icon: status,
			title: name,
			subtitle: socialSecurityNumber,
			fix: [bloodGroup, age, waitingList, weight],
			optional: [pvr, urgency, combinedTransplant]
		}),
		[
			status,
			name,
			socialSecurityNumber,
			bloodGroup,
			age,
			waitingList,
			weight,
			pvr,
			urgency,
			combinedTransplant
		]
	);
}

function useStatusColumnDesktop(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "status",
			label: formatMessage(messages.status),
			render({ value, longLabel }: PatientStatus): React.ReactNode {
				return <StatusBadge value={value} label={longLabel} />;
			},
			mapToString({ longLabel }: PatientStatus): string {
				return longLabel;
			}
		}),
		[formatMessage]
	);
}

function useStatusColumnMobile(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "status",
			label: formatMessage(messages.status),
			render({ value, shortLabel }: PatientStatus): React.ReactNode {
				return <StatusBadge value={value} label={shortLabel} />;
			},
			mapToString({ shortLabel }: PatientStatus): string {
				return shortLabel;
			}
		}),
		[formatMessage]
	);
}

function useNameColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "name",
			label: formatMessage(messages.name)
		}),
		[formatMessage]
	);
}

function useSocialSecurityNumberColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "socialSecurityNumber",
			label: formatMessage(messages.socialSecurityNumber),
			render(value: string): React.ReactNode {
				return <SocialSecurityNumber>{value}</SocialSecurityNumber>;
			}
		}),
		[formatMessage]
	);
}

function useAgeColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "age",
			label: formatMessage(messages.ageLabel),
			render(value: number): React.ReactNode {
				return (
					<>
						<b>{value}</b> <i>{formatMessage(messages.ageUnit)}</i>
					</>
				);
			},
			mapToString(value: number): string {
				return `${value} ${formatMessage(messages.ageUnit)}`;
			},
			compare(a: number, b: number): number {
				if (a < b) {
					return -1;
				}
				if (a > b) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}

function useSexColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({ key: "sex", label: formatMessage(messages.sex) }),
		[formatMessage]
	);
}

function useCityColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({ key: "city", label: formatMessage(messages.city) }),
		[formatMessage]
	);
}

function useWeightColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "weight",
			label: formatMessage(messages.weight),
			render: renderNumberEvent,
			mapToString: mapNumberEventToString,
			compare: compareNumberEvents
		}),
		[formatMessage]
	);
}

function useBloodGroupColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "bloodGroup",
			label: formatMessage(messages.bloodGroup)
		}),
		[formatMessage]
	);
}

function usePvrColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "pvr",
			label: formatMessage(messages.pvr),
			render: renderNumberEvent,
			mapToString: mapNumberEventToString,
			compare: compareNumberEvents
		}),
		[formatMessage]
	);
}

function useWaitingListColumnDesktop(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "waitingListDays",
			label: formatMessage(messages.waitingListDaysLabel),
			render(value: number | undefined): React.ReactNode {
				if (value === undefined) {
					return null;
				}
				return (
					<>
						<b>{value}</b>{" "}
						<i>
							{formatMessage(messages.waitingListDaysUnitDesktop)}
						</i>
					</>
				);
			},
			mapToString(value: number | undefined): string {
				return `${value} ${formatMessage(
					messages.waitingListDaysUnitDesktop
				)}`;
			},
			compare(a: number | undefined, b: number | undefined): number {
				if (a === undefined && b === undefined) {
					return 0;
				}
				if (a === undefined) {
					return 1;
				}
				if (b === undefined) {
					return -1;
				}
				if (a < b) {
					return -1;
				}
				if (a > b) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}

function useWaitingListColumnMobile(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "waitingListDays",
			label: formatMessage(messages.waitingListDaysLabel),
			render(value: number | undefined): React.ReactNode {
				if (value === undefined) {
					return "-";
				}
				return (
					<div>
						<b>{value}</b>{" "}
						<i>
							{formatMessage(messages.waitingListDaysUnitMobile)}
						</i>
					</div>
				);
			},
			mapToString(value: number | undefined): string {
				return `${value} ${formatMessage(
					messages.waitingListDaysUnitDesktop
				)}`;
			},
			compare(a: number | undefined, b: number | undefined): number {
				if (a === undefined && b === undefined) {
					return 0;
				}
				if (a === undefined) {
					return 1;
				}
				if (b === undefined) {
					return -1;
				}
				if (a < b) {
					return -1;
				}
				if (a > b) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}

const urgencyToExclamationMarks = {
	[Urgency.NO]: "-",
	[Urgency.LOW]: "!",
	[Urgency.MEDIUM]: "!!",
	[Urgency.HIGH]: "!!!",
	[Urgency.VERY_HIGH]: "!!!!",
	[Urgency.HIGHEST]: "!!!!!"
};

const urgencyToPrio = {
	[Urgency.NO]: 0,
	[Urgency.LOW]: 1,
	[Urgency.MEDIUM]: 2,
	[Urgency.HIGH]: 3,
	[Urgency.VERY_HIGH]: 4,
	[Urgency.HIGHEST]: 5
};

function useUrgencyColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "urgency",
			label: formatMessage(messages.urgency),
			render(value: Urgency | undefined): React.ReactNode {
				if (value === undefined) {
					return "-";
				}
				return (
					<b style={{ color: "#c23b22" }}>
						{urgencyToExclamationMarks[value]}
					</b>
				);
			},
			mapToString(value: Urgency | undefined): string {
				if (value === undefined) {
					return "";
				}
				return urgencyToExclamationMarks[value];
			},
			compare(a: Urgency, b: Urgency): number {
				const aPrio = urgencyToPrio[a];
				const bPrio = urgencyToPrio[b];
				if (aPrio < bPrio) {
					return -1;
				}
				if (aPrio > bPrio) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}

function useCombinedTransplantColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "combinedTransplant",
			label: formatMessage(messages.combinedTransplant)
		}),
		[formatMessage]
	);
}
